import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ApproveButton, DeclineButton } from "../buttons/button";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import { storeApproveApi, storeRejectApi } from "../../redux/apis/store-api";
import { isEqual } from "lodash";

/**
 * Approve / Decline for one stage of a store (location, LOI, DL, franchise fee).
 *
 * Which button shows is driven by the decision already on record:
 *   nothing decided -> both buttons
 *   approved        -> Approve dimmed and frozen, Decline live
 *   declined        -> both live (Decline again records a new reason)
 *
 * Declining asks for remarks first, so a rejection always carries a reason.
 * Both calls go to the shared /store/approve and /store/reject endpoints, which
 * return the resulting flags — those are handed back through onDecided so the
 * caller can refresh from the server rather than guessing.
 */
/**
 * Each stage stores its rejection reason in its own column, so the note travels
 * under a stage-specific key rather than a shared one. Keyed by the reject flag
 * so call sites pass nothing extra.
 */
const REASON_KEY_BY_REJECT_KEY = {
  is_reject_location: "reject_location_reason",
  is_francise_fee_rejected: "francise_fee_rejected_reason",
  is_loi_rejected: "loi_rejected_reason",
  is_reject_dl: "reject_dl_reason",
  store_agreement_is_reject: "store_agreement_reject_reason",
};

/**
 * Whether the form holds edits the server has not been given yet.
 *
 * Formik's own `dirty` cannot answer this. A decision writes its flags back into
 * the form once the server has saved them, which leaves `dirty` true with
 * nothing genuinely unsaved — the next Approve would then be refused with
 * "Please save your changes before approving", and on a fully paid payment Save
 * is closed too, so the screen deadlocks until it is reloaded. Comparing the
 * values here and skipping the keys the decision owns keeps the guard pointed at
 * real user edits.
 */
const unsavedEditsExist = (formValues, savedValues, decisionKeys) => {
  if (!formValues || !savedValues) return false;
  const owned = new Set(decisionKeys.filter(Boolean));
  const keys = new Set([
    ...Object.keys(formValues),
    ...Object.keys(savedValues),
  ]);
  for (const key of keys) {
    if (owned.has(key)) continue;
    if (!isEqual(formValues[key], savedValues[key])) return true;
  }
  return false;
};

// The buttons' default classes; restated so dimming can be added on top rather
// than replacing the buttons' own styling.
const APPROVE_BUTTON_CLASS =
  "text-027A48 border-51af49 border px-8 py-2 rounded flex items-center gap-1 justify-center fw_600 text-sm";
const DECLINE_BUTTON_CLASS =
  "text-F51D00 border-f51d00 border px-8 py-2 rounded flex items-center gap-1  justify-center fw_600 text-sm";

const ApproveDeclineActions = ({
  storeId,
  approveKey,
  rejectKey,
  isApproved = false,
  isRejected = false,
  onDecided = () => {},
  reasonKey = null,
  // The stage's saved record (the /store/getById store object); the decline
  // reason on record is read from it so it shows while the stage is declined.
  record = null,
  // Unsaved edits must not be approved: the decision is recorded against what
  // the server already holds, so approving a form with pending edits would
  // approve the old record while the screen shows something else. The caller
  // hands over the form's current and last-saved values and the guard works out
  // the difference itself — see unsavedEditsExist.
  formValues = null,
  savedValues = null,
  // Fields a successful decision writes back beyond the approve/reject flags,
  // e.g. the store code Store Details issues or the payment terms flag.
  extraDecisionKeys = [],
  isUploading = false,
  beforeApprove = null,
  afterApprove = null,
  approveText = "Approve",
  declineText = "Decline",
  disabled = false,
  // Keeps Decline usable while the tab is read-only, so a stage can be
  // declined again as often as needed. Approve still follows `disabled`.
  alwaysAllowDecline = false,
  // False hides Decline for a stage that can only be approved.
  showDecline = true,
}) => {
  const [showRemarks, setShowRemarks] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  // Reason from a decline made on this screen. The record prop can lag behind
  // it (a tab save replaces it with a response that may not carry the reason),
  // so the fresh one wins until the page reloads.
  const [latestReason, setLatestReason] = useState(null);

  const reasonField =
    reasonKey || REASON_KEY_BY_REJECT_KEY[rejectKey] || "reason";
  const declineReason = latestReason ?? record?.[reasonField] ?? "";

  const hasUnsavedEdits = unsavedEditsExist(formValues, savedValues, [
    approveKey,
    rejectKey,
    reasonKey,
    REASON_KEY_BY_REJECT_KEY[rejectKey],
    ...extraDecisionKeys,
  ]);

  const guardStore = () => {
    if (!storeId) {
      errorToast("Please save the store first");
      return false;
    }
    return true;
  };

  const handleApprove = async () => {
    if (busy || !guardStore()) return;

    if (isUploading) {
      errorToast("Please wait for the upload to finish before approving");
      return;
    }
    if (hasUnsavedEdits) {
      errorToast("Please save your changes before approving");
      return;
    }

    setBusy(true);

    // Work that must land before the decision is recorded — Store Details issues
    // and saves its store code here. Returning false aborts, so a store is never
    // approved on the back of a step that failed.
    if (beforeApprove) {
      try {
        const proceed = await beforeApprove();
        if (proceed === false) {
          setBusy(false);
          return;
        }
      } catch (err) {
        errorToast(
          err?.response?.data?.message ?? "Could not complete approval",
        );
        setBusy(false);
        return;
      }
    }

    storeApproveApi({ store_id: storeId, [approveKey]: true })
      .then(async (res) => {
        // This backend answers 200 even on failure, with the outcome in the body.
        if (res?.data?.status === false) {
          errorToast(res.data?.message ?? "Failed to approve");
          return;
        }
        successToast(res.data?.message ?? "Approved successfully");
        if (afterApprove) await afterApprove(res.data?.data);
        onDecided(res.data?.data);
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message ?? "Failed to approve");
      })
      .finally(() => setBusy(false));
  };

  const handleDecline = () => {
    if (busy || !guardStore()) return;
    if (!remarks.trim()) {
      errorToast("Please enter the reason for declining");
      return;
    }
    setBusy(true);
    const reasonText = remarks.trim();

    storeRejectApi({
      store_id: storeId,
      [rejectKey]: true,
      [reasonField]: reasonText,
      // Older builds of this endpoint read the note from `reason`; sent
      // alongside so the text lands whichever column the server expects.
      reason: reasonText,
    })
      .then((res) => {
        if (res?.data?.status === false) {
          errorToast(res.data?.message ?? "Failed to decline");
          return;
        }
        successToast(res.data?.message ?? "Declined successfully");
        setShowRemarks(false);
        setRemarks("");
        setLatestReason(res.data?.data?.[reasonField] || reasonText);
        onDecided(res.data?.data);
      })
      .catch((err) => {
        errorToast(err?.response?.data?.message ?? "Failed to decline");
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex gap-5">
          {/* Approve is dimmed and frozen once approved; Decline (unless the
            stage hides it) stays live even when declined, so a stage can be
            declined again with a new reason. */}
          <ApproveButton
            type="button"
            onclkFn={disabled ? () => {} : handleApprove}
            text={isApproved ? "Approved" : approveText}
            disabled={isApproved}
            className={`${APPROVE_BUTTON_CLASS}${
              isApproved || hasUnsavedEdits || isUploading
                ? " opacity-50 cursor-not-allowed"
                : ""
            }`}
          />
          {showDecline && (
            <DeclineButton
              type="button"
              onclkFn={
                disabled && !alwaysAllowDecline
                  ? () => {}
                  : () => setShowRemarks(true)
              }
              text={isRejected ? "Declined" : declineText}
              className={DECLINE_BUTTON_CLASS}
            />
          )}
        </div>
        {isRejected && declineReason && (
          <p className="text-sm text-red-600 max-w-md break-words">
            <span className="font-medium">Decline reason:</span> {declineReason}
          </p>
        )}
      </div>

      {/* Decline remarks */}
      <Transition.Root show={showRemarks} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setShowRemarks(false)}
        >
          <div className="fixed inset-0 overflow-hidden">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
            </Transition.Child>

            {/* Right slide-over, matching the batch panels — a centred popup
                covers the record being judged, which is the thing the reason is
                about. */}
            <div className="fixed inset-0 flex justify-end">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="h-full relative w-[400px] sm:w-[480px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl flex flex-col">
                  <div
                    className="h-[61px] bg-[var(--slide-panel-head-bg)] px-4 flex items-center justify-between border-b flex-shrink-0"
                    style={{ borderColor: "var(--slide-panel-border)" }}
                  >
                    <span
                      style={{
                        color: "var(--slide-panel-h-fg)",
                        fontSize: "var(--slide-panel-h-fs)",
                        fontWeight: "var(--slide-panel-h-fw)",
                      }}
                    >
                      Reason for declining
                    </span>
                    <button type="button" onClick={() => setShowRemarks(false)}>
                      <CloseIcon />
                    </button>
                  </div>

                  <div className="flex-1 px-5 py-5">
                    <label
                      htmlFor="decline_remarks"
                      className="whitespace-nowrap flex text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)] mb-2"
                    >
                      Remarks
                    </label>
                    <textarea
                      id="decline_remarks"
                      rows={4}
                      maxLength={255}
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                      placeholder="Why is this being declined?"
                      className="w-full rounded-[8px] px-2 py-2 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--input-color)] text-[length:var(--input-font-size)] focus:outline-none focus:border-[var(--input-focus)]"
                    />
                    <p className="text-[length:var(--input-place-font-size)] text-[var(--input-place-color)] mt-2">
                      This reason is recorded with the rejection.
                    </p>
                  </div>

                  <div className="px-5 py-4 border-t flex justify-end gap-3 mt-auto flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowRemarks(false)}
                      className="text-primary border border-primary px-4 py-2 rounded-md text-sm"
                    >
                      Cancel
                    </button>
                    <AddButton
                      prefixIcon={false}
                      text="Decline"
                      type="button"
                      loading={busy}
                      onclkFn={handleDecline}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default ApproveDeclineActions;
